import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_SCHOLAR, validateScholar } from '../../lib/scholars.js';
import { findSimilarScholars } from '../../lib/findSimilar.js';
import { useSimilarCheck } from '../../hooks/useSimilarCheck.js';
import SimilarEntriesWarning from '../common/SimilarEntriesWarning.jsx';

export default function ScholarForm({ initialValue, submitLabel, onSubmit, onCancel, busy = false }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(() => ({ ...EMPTY_SCHOLAR, ...initialValue }));
  const [error, setError] = useState(null);

  // Only reset the form when the entry being edited actually changes
  // (different id), not whenever the parent passes a new object reference
  // for the same entry. Without the .id narrowing, anything that caused
  // ScholarsEdit to refetch — including supabase-js's SIGNED_IN event on
  // tab refocus — would replace `scholar` with a new ref and wipe the
  // user's in-progress edits.
  useEffect(() => {
    if (initialValue) setForm({ ...EMPTY_SCHOLAR, ...initialValue });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue?.id]);

  function update(patch) {
    setForm((prev) => ({ ...prev, ...patch }));
    setError(null);
  }

  // Debounced duplicate detection — see SimilarEntriesWarning. Looks
  // for existing scholars with a similar name or matching email.
  const { candidates: similarCandidates } = useSimilarCheck(
    { name: form.name, email: form.email_public, excludeId: initialValue?.id },
    findSimilarScholars,
  );

  async function handleSubmit(event) {
    event.preventDefault();
    const errKey = validateScholar(form);
    if (errKey) {
      setError(t(errKey));
      return;
    }
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err.message ?? String(err));
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <Field label={t('scholars.form.name')} hint={t('scholars.form.nameHint')} required>
        <Input value={form.name} onChange={(v) => update({ name: v })} placeholder="Ricoeur, Paul" />
      </Field>

      <Field label={t('scholars.form.position')}>
        <Input value={form.position} onChange={(v) => update({ position: v })} />
      </Field>

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('scholars.form.department')}>
          <Input value={form.department} onChange={(v) => update({ department: v })} />
        </Field>
        <Field label={t('scholars.form.university')}>
          <Input value={form.university} onChange={(v) => update({ university: v })} />
        </Field>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Field label={t('scholars.form.city')}>
          <Input value={form.city} onChange={(v) => update({ city: v })} />
        </Field>
        <Field label={t('scholars.form.country')}>
          <Input value={form.country} onChange={(v) => update({ country: v })} />
        </Field>
      </div>

      <Field label={t('scholars.form.website')}>
        <Input type="text" inputMode="url" value={form.website} onChange={(v) => update({ website: v })} placeholder="https://…" />
      </Field>

      <Field label={t('scholars.form.email')} hint={t('scholars.form.emailHint')}>
        <Input type="email" value={form.email_public} onChange={(v) => update({ email_public: v })} />
      </Field>

      <Field label={t('scholars.form.bibliographyLink')} hint={t('scholars.form.bibliographyLinkHint')}>
        <Input value={form.bibliography_link} onChange={(v) => update({ bibliography_link: v })} placeholder="/bibliography?author=Ricoeur" />
      </Field>

      <SimilarEntriesWarning candidates={similarCandidates} kind="scholars" />

      {error && (
        <p className="rounded-md border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button type="button" className="btn-ghost" onClick={onCancel} disabled={busy}>
          {t('common.cancel')}
        </button>
        <button type="submit" className="btn-primary" disabled={busy}>
          {busy ? t('common.loading') : submitLabel}
        </button>
      </div>
    </form>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-[0.12em] text-ink-secondary">
        {label}
        {required && <span className="ml-1 text-gold">*</span>}
      </span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-muted">{hint}</span>}
    </label>
  );
}

function Input({ value, onChange, ...rest }) {
  return (
    <input
      {...rest}
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className="form-input"
    />
  );
}
